import apiClient from '../client'
import type { Prompt, PromptFilters, PromptType, PaginatedResponse } from '@/types'

// Backend response type
interface BackendPromptsResponse {
  prompts: Prompt[]
  total: number
}

// Transform backend response to frontend PaginatedResponse format
const transformPromptsResponse = (
  backendData: BackendPromptsResponse,
  page: number,
  pageSize: number
): PaginatedResponse<Prompt> => ({
  items: backendData.prompts || [],
  total: backendData.total || 0,
  page: page,
  page_size: pageSize,
  total_pages: Math.ceil((backendData.total || 0) / pageSize),
})

export interface CreatePromptData {
  type: PromptType
  name: string
  content: string
  version?: string
  is_active?: boolean
  is_default?: boolean
  ab_group?: string
  ab_weight?: number
}

export interface UpdatePromptData {
  name?: string
  content?: string
  version?: string
  is_active?: boolean
  is_default?: boolean
  ab_group?: string
  ab_weight?: number
}

export interface GeneratePromptRequest {
  prompt_type: PromptType
  description: string
  tone?: string
  target_audience?: string
  key_points?: string[]
  constraints?: string[]
}

export interface GeneratePromptResponse {
  success: boolean
  generated: {
    prompt_content: string
    variables_schema: Record<string, string>
    suggested_version: string
    testing_suggestions: string[]
  }
}

export const promptsApi = {
  getAll: async (filters: PromptFilters = {}) => {
    const page = filters.page || 1
    const pageSize = filters.page_size || 25

    // Transform frontend filters to backend params
    // Backend expects: prompt_type, active_only
    // Frontend sends: type, is_active, search
    const backendParams: Record<string, string | boolean | number | undefined> = {}
    if (filters.type) {
      backendParams.prompt_type = filters.type
    }
    if (filters.is_active !== undefined) {
      backendParams.active_only = filters.is_active
    }
    // Note: search is not supported by backend - filtering will be done client-side

    const response = await apiClient.get<BackendPromptsResponse>('/api/prompts', { params: backendParams })

    // Apply client-side search filter if provided (backend doesn't support it)
    let filteredPrompts = response.data.prompts || []
    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filteredPrompts = filteredPrompts.filter(prompt =>
        prompt.name?.toLowerCase().includes(searchLower) ||
        (prompt.prompt_content || prompt.content || '').toLowerCase().includes(searchLower)
      )
    }

    return {
      ...response,
      data: transformPromptsResponse({ prompts: filteredPrompts, total: filteredPrompts.length }, page, pageSize),
    }
  },

  getById: (id: string) =>
    apiClient.get<Prompt>(`/api/prompts/${id}`),

  getTypes: () =>
    apiClient.get<{ prompt_types: Array<{ type: string; description: string }> }>('/api/prompts/types'),

  getActive: (promptType: PromptType) =>
    apiClient.get<Prompt>(`/api/prompts/active/${promptType}`),

  create: (data: CreatePromptData) =>
    apiClient.post<{ success: boolean; prompt: Prompt }>('/api/prompts', {
      prompt_type: data.type,
      name: data.name,
      prompt_content: data.content,
      version: data.version || '1.0',
      is_default: data.is_default,
      ab_test_group: data.ab_group,
      ab_test_weight: data.ab_weight,
    }),

  update: (id: string, data: UpdatePromptData) =>
    apiClient.put<{ success: boolean; prompt: Prompt }>(`/api/prompts/${id}`, {
      name: data.name,
      prompt_content: data.content,
      version: data.version,
      is_default: data.is_default,
      ab_test_group: data.ab_group,
      ab_test_weight: data.ab_weight,
    }),

  delete: (id: string) =>
    apiClient.delete(`/api/prompts/${id}`),

  activate: (id: string, deactivateOthers?: boolean, approvedBy?: string) =>
    apiClient.post<{ success: boolean; prompt: Prompt }>(`/api/prompts/${id}/activate`, {
      deactivate_others: deactivateOthers ?? true,
      approved_by: approvedBy
    }),

  deactivate: (id: string) =>
    apiClient.post<{ success: boolean; prompt: Prompt }>(`/api/prompts/${id}/deactivate`),

  setDefault: (id: string) =>
    apiClient.post<{ success: boolean; prompt: Prompt }>(`/api/prompts/${id}/activate`, {
      deactivate_others: true
    }),

  generate: (data: GeneratePromptRequest) =>
    apiClient.post<GeneratePromptResponse>('/api/prompts/generate', data),

  getAnalytics: (promptType: PromptType, days?: number) =>
    apiClient.get<{ prompt_type: string; period_days: number; analytics: unknown }>(`/api/prompts/analytics/${promptType}`, {
      params: days ? { days } : undefined
    }),
}
